import { ratesByState } from '@/app/(marketplace)/quote-builder/electricity-rates/rates';

import { applyBattery } from './applyBattery';
import { applyHeatPump } from './applyHeatPump';
import { applyInsulation } from './applyInsulation';
import { applySmartHome } from './applySmartHome';
import { applySolar } from './applySolar';
import {
  BATTERY_USABLE_KWH,
  DEFAULT_HOME_SQFT,
  MONTHLY_FIXED_CHARGE_USD,
  SOLAR_EXPORT_RATE_FACTOR,
} from './assumptions';
import { computePayback } from './computePayback';
import { disaggregateLoad } from './disaggregateLoad';
import { consumptionKwhForMonth, splitSolarMonth } from './solarSplit';
import { EnergyLoads, MeasureCost, MonthlyCost, SimulationContext, SimulationInput, SimulationResult } from './types';

const latestRatePerKwhUsd = (stateCode: string): number => {
  const { rates } = ratesByState[stateCode];
  return rates[rates.length - 1].rate / 100;
};

const priceMonth = (loads: EnergyLoads, ctx: SimulationContext, month: number): MonthlyCost => {
  const consumptionKwh = consumptionKwhForMonth(loads, month);
  const productionKwh = loads.solarKwh[month];
  const batterySavingsUsd = loads.batterySavingsUsd[month];

  const { exportKwh, importKwh } = splitSolarMonth(consumptionKwh, productionKwh);
  const gridUsd = importKwh * ctx.ratePerKwhUsd - exportKwh * ctx.ratePerKwhUsd * SOLAR_EXPORT_RATE_FACTOR;

  // The fixed-charge floor only kicks in once solar or a battery is changing
  // the bill; an untouched scenario must price identically to the baseline.
  const hasFloor = productionKwh > 0 || batterySavingsUsd > 0;
  const electricUsd = hasFloor ? Math.max(gridUsd - batterySavingsUsd, MONTHLY_FIXED_CHARGE_USD) : gridUsd;
  const fossilUsd = loads.fossilHeatUsd[month];

  return {
    electricKwh: consumptionKwh,
    electricUsd,
    fossilUsd,
    month,
    totalUsd: electricUsd + fossilUsd,
  };
};

const priceMonths = (loads: EnergyLoads, ctx: SimulationContext): MonthlyCost[] =>
  loads.baseloadKwh.map((_, month) => priceMonth(loads, ctx, month));

const annualTotalUsd = (months: MonthlyCost[]): number => months.reduce((total, { totalUsd }) => total + totalUsd, 0);

export const runSimulation = (input: SimulationInput): SimulationResult => {
  const ratePerKwhUsd = latestRatePerKwhUsd(input.stateCode);
  const ctx: SimulationContext = {
    climateRegion: input.climateRegion,
    heatingFuel: input.heatingFuel,
    homeSqft: input.homeSqft ?? DEFAULT_HOME_SQFT,
    ratePerKwhUsd,
    stateCode: input.stateCode,
    toggles: input.toggles,
  };

  const baselineLoads = disaggregateLoad(ctx, input.monthlyBillUsd);
  const baselineMonths = priceMonths(baselineLoads, ctx);

  const measureCosts: MeasureCost[] = [];
  let loads = baselineLoads;
  let solarSizeKw: number | null = null;

  const collect = ({ loads: nextLoads, measureCost }: { loads: EnergyLoads; measureCost: MeasureCost }): void => {
    loads = nextLoads;
    if (measureCost.grossUsd > 0) {
      measureCosts.push(measureCost);
    }
  };

  // Deterministic order: envelope before heat pump conversion, solar sizing
  // after loads are set, battery after solar so self-use sees the export profile.
  if (ctx.toggles.insulation) {
    collect(applyInsulation(loads, ctx));
  }
  if (ctx.toggles.smartHome) {
    collect(applySmartHome(loads, ctx));
  }
  if (ctx.toggles.heatPump) {
    collect(applyHeatPump(loads, ctx));
  }
  if (ctx.toggles.solar) {
    const solar = applySolar(loads, ctx);
    collect(solar);
    solarSizeKw = solar.sizeKw;
  }
  if (ctx.toggles.battery) {
    collect(applyBattery(loads, ctx));
  }

  const scenarioMonths = priceMonths(loads, ctx);
  const annualSavingsUsd = annualTotalUsd(baselineMonths) - annualTotalUsd(scenarioMonths);

  const upfrontGrossUsd = measureCosts.reduce((total, { grossUsd }) => total + grossUsd, 0);
  const upfrontNetUsd = measureCosts.reduce((total, { netUsd }) => total + netUsd, 0);

  const scenarioAnnualKwh = scenarioMonths.reduce((total, { electricKwh }) => total + electricKwh, 0);
  const backupHours = ctx.toggles.battery ? (BATTERY_USABLE_KWH / (scenarioAnnualKwh / 365)) * 24 : null;

  return {
    annualSavingsUsd,
    assumptions: {
      annualKwh: (input.monthlyBillUsd * 12) / ratePerKwhUsd,
      backupHours,
      ratePerKwhUsd,
    },
    baselineMonths,
    measureCosts,
    paybackYears: computePayback(annualSavingsUsd, upfrontNetUsd),
    scenarioMonths,
    solarSizeKw,
    upfrontGrossUsd,
    upfrontNetUsd,
  };
};
